/**
 * LockIndicator. A shared grid-template-standard lock glyph that, on hover or
 * focus, explains why a record is locked. The lock guards AUTOMATION (the email
 * extractor and the automatic GL refresh will not overwrite a manually edited
 * row); it does NOT make the record read-only. A human can still edit it on the
 * detail page. The default tooltip text says exactly that so the meaning is
 * never ambiguous.
 *
 * SHARED component (COCKPIT.md section 2.10). Used on the Finance Invoices grid
 * (per row) and detail (next to the Status). Hover and keyboard focus both
 * reveal the tooltip; Escape and blur hide it. The glyph is muted ink and sized
 * to sit inline with row text.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { Lock } from 'lucide-react';

// The canonical lock explanation. Kept here as the single source so every
// surface that shows the lock says the same thing (COCKPIT.md section 2.3 one
// canonical source for help text).
export const LOCKED_TOOLTIP =
  'Locked: manually edited. The email extractor and the automatic GL refresh ' +
  'will not overwrite this. You can still edit it here.';

export function LockIndicator({
  locked,
  text = LOCKED_TOOLTIP,
}: {
  // Truthy when the record is locked. Tolerant of number/boolean/null so it can
  // take the api's 0/1 or a boolean directly.
  locked: number | boolean | null | undefined;
  // Override the tooltip copy (defaults to LOCKED_TOOLTIP).
  text?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const tipId = useId();

  // Click-outside + Escape close (so a tap on touch / a stray click dismisses).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!locked) return null;

  return (
    <span
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Why is this locked?"
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center text-amp-ink-muted hover:text-amp-ink focus:outline-none focus:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink dark:focus:text-amp-dark-ink"
      >
        <Lock className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <span
          id={tipId}
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          className="absolute left-0 top-full z-50 mt-1 w-[260px] rounded-md border border-amp-line bg-amp-white p-2.5 text-xs leading-snug text-amp-ink shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          {text}
        </span>
      )}
    </span>
  );
}
